/*
Created-by: XtroEdge AI
Updated-by: XtroEdge AI
Last modified: 2026-11-03T22:30:00+05:00
Intent: The Receipt submissions card on a campaign's detail page: every receipt uploaded to a receipt level, with its image, what the reader found (shop, date, total, products), each rule's result, and approve / reject for the ones waiting for a person.
*/

import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { firstValueFrom } from 'rxjs';
import { PagesService } from '@services/pages.service';
import { SharedService } from '@services/shared.service';
import { isArabicLang } from 'src/app/utils/arabic-pdf.util';
import {
  ReceiptRuleResult,
  ReceiptStatusFilter,
  ReceiptSubmission,
  ReceiptSubmissionPage,
  buildReceiptReview,
  readReceiptSubmission,
  readReceiptSubmissionPage,
  receiptStatusParam,
  ruleTally,
} from 'src/app/utils/receipt-submission.util';

/** A receipt level of the campaign, for the level filter and as the name fallback. */
export interface ReceiptLevelOption {
  id: string;
  name: string;
  arabic_name?: string | null;
}

const KNOWN_RULES = [
  'retailer', 'products', 'min_quantity', 'min_spend', 'currency', 'purchase_window',
  'duplicate', 'confidence', 'attempts', 'legibility',
];
const RULE_ALIASES: Record<string, string> = {
  retailers: 'retailer', shop: 'retailer', store: 'retailer', merchant: 'retailer',
  product: 'products', quantity: 'min_quantity', spend: 'min_spend', amount: 'min_spend',
  date: 'purchase_window', purchase_date: 'purchase_window', window: 'purchase_window',
  duplicate_receipt: 'duplicate', max_attempts: 'attempts', readable: 'legibility',
};

@Component({
  selector: 'app-contest-receipt-submissions',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, NzTableModule, NzToolTipModule, NzDrawerModule],
  templateUrl: './contest-receipt-submissions.component.html',
  styleUrls: ['./contest-receipt-submissions.component.scss'],
})
export class ContestReceiptSubmissionsComponent implements OnChanges {
  @Input() contestId: string | null = null;
  @Input() levels: ReceiptLevelOption[] = [];

  private pagesService = inject(PagesService);
  private sharedService = inject(SharedService);
  private translate = inject(TranslateService);

  readonly filters: ReceiptStatusFilter[] = ['', 'review', 'approved', 'rejected'];
  readonly perPage = 10;

  submissions: ReceiptSubmission[] = [];
  total = 0;
  counts: ReceiptSubmissionPage['counts'] = null;
  page = 1;
  filter: ReceiptStatusFilter = '';
  levelId = '';
  available = true;
  loading = false;
  loadFailed = false;

  /** The receipt open in the review drawer. */
  selected: ReceiptSubmission | null = null;
  rejecting = false;
  rejectReason = '';
  deciding: 'approve' | 'reject' | null = null;
  imageFailed = false;

  /** Stops an older page answer from replacing a newer one. */
  private loadToken = 0;

  get isArabic(): boolean {
    return isArabicLang();
  }

  /** Full width on a phone, a side panel elsewhere. */
  get drawerWidth(): string | number {
    return typeof window !== 'undefined' && window.innerWidth < 640 ? '100%' : 560;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contestId'] && this.contestId) this.load(1);
  }

  async load(page: number): Promise<void> {
    if (!this.contestId) return;
    const token = ++this.loadToken;
    this.loading = true;
    this.loadFailed = false;
    try {
      const status = receiptStatusParam(this.filter);
      const response = await firstValueFrom(this.pagesService.getAll('CONTEST_RECEIPT_SUBMISSIONS', {
        contest_id: this.contestId,
        page,
        per_page: this.perPage,
        ...(status ? { status } : {}),
        ...(this.levelId ? { level_id: this.levelId } : {}),
      }));
      if (token !== this.loadToken) return;
      const result = readReceiptSubmissionPage(response);
      this.submissions = result.items;
      this.total = result.total;
      this.counts = result.counts;
      this.available = result.available;
      this.page = page;
    } catch (error: any) {
      if (token !== this.loadToken || error?.name === 'EmptyError') return;
      this.submissions = [];
      this.total = 0;
      this.loadFailed = true;
    } finally {
      if (token === this.loadToken) this.loading = false;
    }
  }

  setFilter(filter: ReceiptStatusFilter): void {
    if (this.filter === filter) return;
    this.filter = filter;
    this.load(1);
  }

  setLevel(levelId: string): void {
    this.levelId = levelId;
    this.load(1);
  }

  countFor(filter: ReceiptStatusFilter): number | null {
    if (!this.counts) return filter === this.filter ? this.total : null;
    if (!filter) return this.counts.review + this.counts.approved + this.counts.rejected;
    return this.counts[filter];
  }

  open(submission: ReceiptSubmission): void {
    this.selected = submission;
    this.rejecting = false;
    this.rejectReason = '';
    this.imageFailed = false;
  }

  close(): void {
    if (this.deciding) return;
    this.selected = null;
  }

  canDecide(submission: ReceiptSubmission | null): boolean {
    return !!submission && submission.state === 'review' && this.available;
  }

  startReject(): void {
    this.rejecting = true;
  }

  cancelReject(): void {
    this.rejecting = false;
    this.rejectReason = '';
  }

  async decide(decision: 'approve' | 'reject'): Promise<void> {
    const submission = this.selected;
    if (!submission || !this.contestId || this.deciding || !this.canDecide(submission)) return;
    const body = buildReceiptReview(this.contestId, submission.id, decision, decision === 'reject' ? this.rejectReason : null);
    if (!body) return;
    this.deciding = decision;
    try {
      const response: any = await firstValueFrom(this.pagesService.create('CONTEST_RECEIPT_REVIEW', body));
      const updated = response && typeof response === 'object' && (response.id || response.submission_id)
        ? readReceiptSubmission(response)
        : null;
      const next: ReceiptSubmission = updated?.id === submission.id
        ? updated
        : { ...submission, state: decision === 'approve' ? 'approved' : 'rejected', reason: body.reason ?? submission.reason };
      this.submissions = this.submissions.map((s) => (s.id === submission.id ? next : s));
      this.selected = next;
      this.rejecting = false;
      this.sharedService.createNotification('success',
        this.translate.instant(decision === 'approve' ? 'receiptReview.approved' : 'receiptReview.rejected'));
      // The counts and the filtered list move with the decision.
      this.load(this.page);
    } catch (error) {
      this.sharedService.showError(error, this.translate.instant('receiptReview.decisionFailed'));
    } finally {
      this.deciding = null;
    }
  }

  trackSubmission = (_: number, submission: ReceiptSubmission) => submission.id;

  levelName(submission: ReceiptSubmission): string {
    const own = this.isArabic ? submission.level_name_ar || submission.level_name : submission.level_name || submission.level_name_ar;
    if (own) return own;
    const level = this.levels.find((l) => String(l.id) === String(submission.level_id));
    return (level && (this.isArabic ? level.arabic_name || level.name : level.name || level.arabic_name)) || '—';
  }

  levelOptionName(level: ReceiptLevelOption): string {
    return (this.isArabic ? level.arabic_name || level.name : level.name || level.arabic_name) || '—';
  }

  stateLabel(submission: ReceiptSubmission): string {
    return this.translate.instant(`receiptReview.state.${submission.state}`);
  }

  stateTone(submission: ReceiptSubmission): string {
    switch (submission.state) {
      case 'approved': return 'is-success';
      case 'rejected': return 'is-danger';
      case 'review': return 'is-warning';
      default: return 'is-neutral';
    }
  }

  filterLabel(filter: ReceiptStatusFilter): string {
    return this.translate.instant(`receiptReview.filter.${filter || 'all'}`);
  }

  tally(submission: ReceiptSubmission): { passed: number; total: number } {
    return ruleTally(submission);
  }

  ruleLabel(rule: ReceiptRuleResult): string {
    const raw = rule.rule.toLowerCase().replace(/[\s-]+/g, '_');
    const key = RULE_ALIASES[raw] ?? raw;
    if (KNOWN_RULES.includes(key)) return this.translate.instant(`receiptReview.rules.${key}`);
    const words = rule.rule.replace(/[_-]+/g, ' ').trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
  }

  ruleIcon(rule: ReceiptRuleResult): string {
    if (rule.passed === true) return 'fa-circle-check';
    if (rule.passed === false) return 'fa-circle-xmark';
    return 'fa-circle-minus';
  }

  ruleTone(rule: ReceiptRuleResult): string {
    if (rule.passed === true) return 'is-pass';
    if (rule.passed === false) return 'is-fail';
    return 'is-skip';
  }

  money(value: number | null, currency: string | null): string {
    if (value === null) return '—';
    const amount = value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    return currency ? `${amount} ${currency}` : amount;
  }

  confidenceText(value: number | null): string {
    return value === null ? '—' : `${Math.round(value * 100)}%`;
  }

  /** dd/mm/yy, and the time when there is one — the same in English and Arabic. */
  when(value: string | number | null, withTime = true): string {
    if (value === null || value === undefined || value === '') return '—';
    const dayOnly = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim());
    const date = dayOnly ? new Date(`${(value as string).trim()}T00:00:00`) : new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    const pad = (n: number) => String(n).padStart(2, '0');
    const day = `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${String(date.getFullYear()).slice(2)}`;
    return withTime && !dayOnly ? `${day}, ${pad(date.getHours())}:${pad(date.getMinutes())}` : day;
  }
}
